import { createContext, useContext, useEffect, useState } from 'react'
import { NavLink, useMatch, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Monitor, UtensilsCrossed, Building2,
  ShoppingBag, ImageIcon, Users, Palette,
  ChevronDown, ChevronUp, Check, Plus, ShieldCheck, MessageCircle, Sun, Moon, Globe, BookOpen,
  LogOut, User, ListOrdered, LayoutGrid, Tag, Layers,
  CreditCard, UserCircle, Layers3, Sparkles, Music, ListChecks, ArrowLeftRight, Tablet,
  MonitorSmartphone, BarChart3, Ticket, Warehouse, Truck,
  Settings,
  ChefHat,
  Handshake,
} from 'lucide-react'
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel,
  SidebarGroupContent, SidebarMenu, SidebarMenuItem,
  SidebarMenuButton, SidebarHeader, SidebarFooter, SidebarTrigger,
} from '../ui/sidebar'
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { applyLanguage, LANGUAGES, type LangCode } from '../../i18n/index'
import { cn } from '../../lib/utils'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { ACCENTS } from '../../lib/accents'
import { useBranchFilter } from '../../contexts/BranchFilterContext'
import { useEntitlements } from '../../hooks/useEntitlements'
import { usePermissions } from '../../hooks/usePermissions'
import { WetookMark } from '../WetookMark'
import { PocketMark } from '../PocketMark'

// ── Cross-app URL config ────────────────────────────────────────────────────
// Each app sets its own URL to '' and other apps to their production base URL.
// Resolved at Vite build time per app from the app's .env.production.
const APP_URLS = {
  signage: ((import.meta as any).env?.VITE_FRAME_URL as string || '').replace(/\/$/, ''),
  page:    ((import.meta as any).env?.VITE_PING_URL    as string || '').replace(/\/$/, ''),
  my:      ((import.meta as any).env?.VITE_MY_URL      as string || '').replace(/\/$/, ''),
  pos:     ((import.meta as any).env?.VITE_POS_URL     as string || '').replace(/\/$/, ''),
}

// An app is "the current app" when it left its own VITE_*_URL empty. We ALSO
// treat a URL equal to the current origin as the current app, so that
// accidentally setting an app's OWN url (e.g. VITE_PING_URL on the page app)
// can't make it fail to recognise itself and silently drop its entire nav.
const ORIGIN = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''
const isCurrentApp = (url: string) => url === '' || url === ORIGIN

// ── Nav config ──────────────────────────────────────────────────────────────
// `app` marks which sub-app owns this route. NavItem renders a NavLink for
// local routes (APP_URLS[app] === '') and a plain <a> for cross-app ones.

// Signage nav, split into labelled sections. Overview + Screens sit at the top
// ungrouped; content-authoring lives under "Content" (Content Hub, where
// playlists/layouts are built, plus Media, Audio, Transitions); the interactive
// touch surface (Stop Screens) sits under "Tablet"; Activity + Tutorials trail
// at the bottom, ungrouped.
// `perm` (optional) hides the item when the user's role lacks that capability
// — belt-and-suspenders with the backend (which 403s regardless). Items with no
// perm are always shown.
const navSignageTop = [
  { key: 'overview',   href: '/overview',         icon: LayoutDashboard, app: 'signage' as const, perm: 'dashboard:view' },
  { key: 'screens',    href: '/screens',          icon: Monitor,         app: 'signage' as const, perm: 'screens:view' },
]
const navSignageContent = [
  { key: 'contentHub',  href: '/content-hub',      icon: Layers,          app: 'signage' as const, perm: 'playlists:view' },
  { key: 'media',       href: '/management/media', icon: ImageIcon,       app: 'signage' as const, perm: 'media:view' },
  { key: 'audioHub',    href: '/audio-hub',        icon: Music,           app: 'signage' as const, perm: 'media:view' },
  { key: 'transitions', href: '/transitions',      icon: ArrowLeftRight,  app: 'signage' as const, perm: 'playlists:view' },
]
// "Tablet" is now a destination of its own (a page with Stop Screens as a tab),
// not a section header over a single Stop Screens link.
const navSignageTablet = [
  { key: 'tablet',      href: '/tablet',           icon: Tablet,          app: 'signage' as const, perm: 'screens:view' },
]
const navSignageBottom = [
  { key: 'activity',    href: '/activity',         icon: ListChecks,      app: 'signage' as const, perm: 'dashboard:view' },
  { key: 'tutorials',   href: '/tutorials',        icon: BookOpen,        app: 'signage' as const },
  { key: 'frameSettings', href: '/settings',       icon: Settings,        app: 'signage' as const },
]

// Top-level (ungrouped) nav for the my-app — Dashboard replaces Overview.
// Users / Roles / Organization are flat siblings, not nested in a group.
// `organization` is filtered out for non-manager roles at render time.
const navGeneralMy = [
  { key: 'myDashboard',  href: '/',                 icon: LayoutDashboard, app: 'my' as const },
  { key: 'personalInfo', href: '/personal-info',    icon: UserCircle,      app: 'my' as const },
  { key: 'subscription', href: '/subscription',     icon: CreditCard,      app: 'my' as const },
  { key: 'users',        href: '/management/users', icon: Users,           app: 'my' as const },
  { key: 'roles',        href: '/management/roles', icon: ShieldCheck,     app: 'my' as const },
  { key: 'organization', href: '/organization',     icon: Building2,       app: 'my' as const, managerOnly: true },
  { key: 'branches',     href: '/branches',         icon: Layers3,         app: 'my' as const, managerOnly: true },
]

const MANAGER_ROLES = new Set(['owner', 'admin'])

// Cashier back-office nav. The till itself (/register) is deliberately NOT a
// sidebar item — it is a full-bleed surface with no shell, reached from the
// dashboard's "Open register" action instead. Devices moved here from the My
// app because a terminal is a Cashier object, and Products sits here because
// Cashier owns the catalog the till sells from (Ping renders the same rows).
// What the till itself is for: today's numbers, the orders behind them, and
// the reports built on top. These stay flat and unlabelled — they are the
// reason someone opens the back office, and putting them behind a heading
// makes the common case a click deeper.
const navPos = [
  { key: 'posDashboard', href: '/',        icon: LayoutDashboard, app: 'pos' as const },
  { key: 'posOrders',    href: '/orders',  icon: ListOrdered,     app: 'pos' as const, perm: 'pos:reports' },
  { key: 'posReports',   href: '/reports', icon: BarChart3,       app: 'pos' as const, perm: 'pos:reports' },
]

// ── The three groups below ──────────────────────────────────────────────────
// The back office had nine flat items, which is where a nav stops being a list
// and becomes a search. They divide cleanly by the question each answers:
// what do we sell, what do we buy, and how is this place set up. Grouped the
// way Marketing already is, so the shape of the nav is one idea rather than a
// flat run with one labelled exception at the bottom.

/** What is sold. Products and the choices attached to them. */
const navPosMenu = [
  { key: 'products', href: '/management/products', icon: ShoppingBag, app: 'pos' as const },
  { key: 'addons',   href: '/management/addons',   icon: Layers,      app: 'pos' as const },
  { key: 'deliveryApps', href: '/management/delivery-apps', icon: Truck, app: 'pos' as const },
]

/** What is bought. The shelf, and everything that moves stock on or off it. */
const navPosProcurement = [
  { key: 'storage',    href: '/storage',            icon: Warehouse, app: 'pos' as const },
  { key: 'purchasing', href: '/storage/purchasing', icon: Truck,     app: 'pos' as const },
  { key: 'production', href: '/storage/production', icon: ChefHat,   app: 'pos' as const },
  { key: 'deliveries', href: '/storage/deliveries', icon: ArrowLeftRight, app: 'pos' as const },
  { key: 'suppliers',  href: '/storage/suppliers',  icon: Handshake, app: 'pos' as const },
]

/** How the place is set up. Both gated on pos:operate, as they were flat. */
const navPosManagement = [
  { key: 'devices',     href: '/devices',  icon: MonitorSmartphone, app: 'pos' as const, perm: 'pos:operate' },
  { key: 'posSettings', href: '/settings', icon: Settings,          app: 'pos' as const, perm: 'pos:operate' },
]

/**
 * Ops mode: running the place rather than selling from it — today's
 * checklists and the shelf. Sales mode is everything above (2026-11-01).
 */
const navPosOps = [
  { key: 'opsOverview', href: '/operations',            icon: LayoutDashboard, app: 'pos' as const, perm: 'pos:operate' },
  { key: 'checklists',  href: '/operations/checklists', icon: ListChecks,      app: 'pos' as const, perm: 'pos:operate' },
]

/** Paths that belong to Ops mode; everything else in Cashier is Sales. */
const OPS_PATHS = ['/operations', '/storage']
export const isOpsPath = (path: string) => OPS_PATHS.some(p => path === p || path.startsWith(`${p}/`))

/**
 * The Sales | Ops switch at the top of Cashier's nav. The mode follows the
 * page you are on, so a link or a refresh never shows the wrong half; the
 * switch itself just goes to that mode's home.
 */
function PosModeSwitch() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const ops = isOpsPath(pathname)
  const seg = (active: boolean) =>
    `h-7 flex-1 rounded-md text-xs font-semibold transition ${active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
  return (
    <div className="mx-1 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 group-data-[collapsible=icon]:hidden" data-testid="pos-mode-switch">
      <button type="button" className={seg(!ops)} aria-pressed={!ops} onClick={() => { if (ops) navigate('/') }}>
        {t('nav.salesMode', 'Sales')}
      </button>
      <button type="button" className={seg(ops)} aria-pressed={ops} onClick={() => { if (!ops) navigate('/operations') }}>
        {t('nav.opsMode', 'Ops')}
      </button>
    </div>
  )
}

// Marketing, as a collapsible group in the reference. Only the two kinds the
// data model actually distinguishes: an Offer applies automatically to anyone
// who qualifies, a Coupon has to be presented. Gift cards are stored value —
// a balance and a redemption ledger — and are deliberately not stubbed here.
const navPosMarketing = [
  { key: 'offers',  href: '/management/offers',  icon: Tag,   app: 'pos' as const },
  { key: 'coupons', href: '/management/coupons', icon: Ticket, app: 'pos' as const },
]

const navPing = [
  { key: 'pingDashboard', href: '/ping',        icon: LayoutDashboard, app: 'page' as const },
  { key: 'build',           href: '/ping/build',  icon: LayoutGrid,      app: 'page' as const },
  { key: 'customize',       href: '/ping/design', icon: Palette,         app: 'page' as const },
  { key: 'dynamics',        href: '/ping/customization', icon: Sparkles, app: 'page' as const },
]

// ── NavItem ─────────────────────────────────────────────────────────────────
type AppKey = keyof typeof APP_URLS

// The "current app" is whichever app set its own VITE_*_URL to ''.
// Each app gets a focused sidebar:
//   • my.wetook       → account / org / users / roles
//   • signage.wetook  → overview / screens / visuals / settings
//   • ping.wetook     → Ping builder only (catalog, orders and offers live in Cashier)
const IS_MY_APP      = isCurrentApp(APP_URLS.my)
const IS_SIGNAGE_APP = isCurrentApp(APP_URLS.signage)
const IS_PAGE_APP    = isCurrentApp(APP_URLS.page)
const IS_POS_APP     = isCurrentApp(APP_URLS.pos)

// Shared button styling — the active item is a filled pill in the sidebar's own
// selection tone (--sidebar-primary: brand-black in light, white in dark), so it
// stays high-contrast in both themes and reads identically across every app
// (rather than each app's --primary, which could be a dark accent that vanishes
// on the dark sidebar). Ghost on hover.
const NAV_BTN_BASE  = 'h-10 px-3 gap-3 rounded-lg text-sm font-medium transition-colors'
const NAV_BTN_IDLE  = 'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground'
const NAV_BTN_ACTIVE = 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground shadow-sm'

function NavItem({ href, icon: Icon, label, app }: {
  href: string
  icon: typeof Monitor
  label: string
  app: AppKey
}) {
  // Empty OR equal-to-current-origin → treat as a LOCAL route (NavLink), so a
  // mis-set own-app URL still navigates in-app instead of a full cross-origin reload.
  const baseUrl = isCurrentApp(APP_URLS[app]) ? '' : APP_URLS[app]
  const match = useMatch({ path: href, end: true })
  const isActive = !!match && !baseUrl

  if (baseUrl) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={label}
          data-active={false}
          className={cn(NAV_BTN_BASE, NAV_BTN_IDLE)}
        >
          <a href={`${baseUrl}${href}`}>
            <Icon className="w-[18px] h-[18px] shrink-0" />
            <span className="group-data-[collapsible=icon]:hidden flex-1 min-w-0 truncate">
              {label}
            </span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={label}
        data-active={isActive}
        className={cn(NAV_BTN_BASE, isActive ? NAV_BTN_ACTIVE : NAV_BTN_IDLE)}
      >
        <NavLink to={href}>
          <Icon className="w-[18px] h-[18px] shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden flex-1 min-w-0 truncate">
            {label}
          </span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

// ── NavGroup ────────────────────────────────────────────────────────────────
// Flat (non-collapsible) group. If the group's label is set and one of the
// group's routes matches the current URL, the label renders as a "selection
// pill" — a subtle highlighted chip — to mirror the active nav-item pill.
/**
 * Which collapsible group is open — one at a time.
 *
 * The groups used to open independently, so a sidebar with four of them
 * expanded was a scroll of forty rows on a phone. Opening a group now closes
 * the others (an accordion); the group holding the current page opens itself
 * on arrival so the page is never hidden.
 */
const OpenGroupContext = createContext<{
  openGroup: string | null
  setOpenGroup: (key: string | null) => void
}>({ openGroup: null, setOpenGroup: () => {} })

function NavGroup({
  label,
  icon: Icon,
  items,
  collapsible = false,
  defaultOpen = false,
}: {
  label?: string
  icon?: any
  items: any[]
  // When true the label becomes a toggle that expands/collapses the items.
  collapsible?: boolean
  defaultOpen?: boolean
}) {
  const { t } = useTranslation()
  const location = useLocation()

  // The group is "selected" if any of its local (same-app) routes matches.
  const isGroupActive = items.some((it) => {
    if (APP_URLS[it.app as AppKey] !== '') return false
    return location.pathname === it.href || location.pathname.startsWith(it.href + '/')
  })

  // Collapsible groups are an accordion (see OpenGroupContext). The group
  // holding the active route opens itself when the route lands in it.
  const { openGroup, setOpenGroup } = useContext(OpenGroupContext)
  const groupKey = label ?? ''
  useEffect(() => {
    if (collapsible && (isGroupActive || defaultOpen) && openGroup === null) setOpenGroup(groupKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupActive])
  const open = !collapsible || openGroup === groupKey
  const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(open) : next
    setOpenGroup(value ? groupKey : null)
  }
  const showItems = !collapsible || open

  const labelInner = (
    <>
      {Icon && (
        <Icon
          className={cn(
            'w-4 h-4 transition-colors',
            isGroupActive ? 'text-primary' : 'text-sidebar-foreground/50',
          )}
        />
      )}
      <SidebarGroupLabel
        className={cn(
          'flex-1 text-start text-[10px] uppercase tracking-widest font-semibold h-auto py-0 px-0 transition-colors',
          isGroupActive ? 'text-primary' : 'text-sidebar-foreground/55',
        )}
      >
        {label}
      </SidebarGroupLabel>
      {collapsible && (
        open
          ? <ChevronUp className="w-3.5 h-3.5 shrink-0 text-sidebar-foreground/50" />
          : <ChevronDown className="w-3.5 h-3.5 shrink-0 text-sidebar-foreground/50" />
      )}
    </>
  )

  return (
    // Labelled groups get a larger top gap to set off the section header;
    // header-less groups (the signage nav) use a small, even gap so every nav
    // item is spaced consistently instead of leaving a big blank where a
    // heading used to be.
    <SidebarGroup className={cn('p-0 first:mt-0', label ? 'mt-4' : 'mt-0')}>
      {label && (
        collapsible ? (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className={cn(
              'flex items-center gap-2 w-full group-data-[collapsible=icon]:hidden px-2 py-1 rounded-md transition-colors hover:bg-sidebar-accent/40',
              isGroupActive && 'bg-sidebar-accent/60',
            )}
          >
            {labelInner}
          </button>
        ) : (
          <div
            className={cn(
              'flex items-center gap-2 group-data-[collapsible=icon]:hidden px-2 py-1 rounded-md transition-colors',
              isGroupActive && 'bg-sidebar-accent/60',
            )}
          >
            {labelInner}
          </div>
        )
      )}
      {showItems && (
        <SidebarGroupContent>
          <SidebarMenu className={cn('gap-1.5', label && 'mt-1')}>
            {items.map(({ key, href, icon, app }) => (
              <NavItem key={key} href={href} icon={icon} label={t(`nav.${key}`)} app={app} />
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

// ── BranchFilterCards ─────────────────────────────────────────────────────────
// "Branches" picker styled after the Space dropdown reference: a trigger card
// that shows the current selection (single name, or "N branches" summary) and
// a chevron, with a collapsible panel beneath it listing every accessible
// branch as a row + checkmark. Clicking a row toggles that branch in/out of
// the filter — the last remaining enabled branch is locked on so the filter
// can never go empty. Bottom of the panel has cross-app links into the my-app
// branches management page. Hidden entirely when the user has no branches,
// and collapsed away in icon-rail mode where there's no width.
function BranchFilterCards() {
  const { t } = useTranslation()
  const { branches, loading, isEnabled, toggle } = useBranchFilter()
  const [open, setOpen] = useState(false)

  if (loading || branches.length === 0) return null

  const enabled = branches.filter(b => isEnabled(b.id))
  const enabledCount = enabled.length
  // Summary line in the trigger card. One enabled → show its name. Multiple
  // → "N of M branches". This mirrors the Space dropdown reference, where
  // the trigger always reads the active selection at a glance.
  const summary = enabledCount === 1
    ? enabled[0].name
    : enabledCount === branches.length
      ? t('nav.branchesAll', 'All branches ({{count}})', { count: branches.length })
      : t('nav.branchesSome', '{{enabled}} of {{total}} branches', { enabled: enabledCount, total: branches.length })

  // Cross-app links: branches management lives in the my-app. Resolves to a
  // local route when this sidebar is mounted inside my-app, and to a fully
  // qualified URL when mounted elsewhere (signage / page).
  const myUrl = APP_URLS.my
  const branchesHref     = `${myUrl}/branches`
  const branchesNewHref  = `${myUrl}/branches?new=1`

  return (
    <SidebarGroup className="p-0 mt-4 group-data-[collapsible=icon]:hidden">
      {/* Trigger card — mirrors the "Space" card in the reference. Top-row
          label is muted; second row is the current selection summary; a
          chevron on the end flips between up / down as the panel opens. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex items-center gap-2 w-full rounded-xl ring-1 ring-inset ring-[hsl(var(--border-control,var(--sidebar-border)))] bg-sidebar-accent/30 hover:bg-sidebar-accent transition-colors px-3 py-2 text-start"
      >
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/55 leading-none">
            {t('nav.branchesFilter', 'Branches')}
          </p>
          <p className="text-[13px] font-medium text-sidebar-foreground truncate mt-1">
            {summary}
          </p>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-sidebar-foreground/60 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-sidebar-foreground/60 shrink-0" />
        }
      </button>

      {/* Expanded panel — only mounted when open so the closed state stays
          out of the tab order. Recent label + branch rows + manage actions. */}
      {open && (
        <SidebarGroupContent className="mt-2">
          <div className="rounded-xl ring-1 ring-inset ring-[hsl(var(--border-control,var(--sidebar-border)))] bg-sidebar-accent overflow-hidden">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/45 px-3 pt-3 pb-1">
              {t('nav.branchesRecent', 'Recent Branches')}
            </p>
            <div className="flex flex-col py-1">
              {branches.map(b => {
                const on = isEnabled(b.id)
                // The one remaining enabled branch is locked on — make that
                // legible to the operator instead of silently no-op'ing.
                const locked = on && enabledCount <= 1
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggle(b.id)}
                    aria-pressed={on}
                    title={locked ? t('nav.branchesFilterLastOn', 'At least one branch must stay enabled') : b.name}
                    className={cn(
                      // Panel bg is now sidebar-accent, so rows hover on a
                      // foreground tint (dark in light mode, light in dark) to
                      // stay legible against it.
                      'flex items-center gap-2.5 w-full px-3 py-1.5 text-start transition-colors hover:bg-sidebar-foreground/10',
                      locked && 'cursor-default',
                    )}
                  >
                    <Building2 className="w-3.5 h-3.5 text-sidebar-foreground/60 shrink-0" />
                    <span className={cn(
                      'flex-1 min-w-0 truncate text-[13px]',
                      on ? 'font-medium text-sidebar-foreground' : 'text-sidebar-foreground/65',
                    )}>
                      {b.name}
                    </span>
                    {/* Checkmark badge — green pill when enabled, mirrors the
                        Space dropdown reference. Empty space when off so the
                        rows still line up. */}
                    {on ? (
                      <span className="w-5 h-5 rounded-md bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                    ) : (
                      <span className="w-5 h-5 shrink-0" />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Footer actions — cross-app links into the my-app branches
                management page. The full-URL fallback (myUrl !== '') is what
                lets this sidebar work the same from signage / page apps. */}
            <div className="border-t border-[hsl(var(--border-filter,var(--sidebar-border))/0.6)] flex flex-col">
              <a
                href={branchesHref}
                className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-sidebar-foreground hover:bg-sidebar-foreground/10 transition-colors"
              >
                <Layers3 className="w-3.5 h-3.5 text-sidebar-foreground/60 shrink-0" />
                <span className="flex-1 min-w-0 truncate">{t('nav.branchesManage', 'Manage branches')}</span>
              </a>
              <a
                href={branchesNewHref}
                className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-sidebar-foreground hover:bg-sidebar-foreground/10 transition-colors"
              >
                <Plus className="w-3.5 h-3.5 text-sidebar-foreground/60 shrink-0" />
                <span className="flex-1 min-w-0 truncate">{t('nav.branchesNew', 'New branch')}</span>
              </a>
            </div>
          </div>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

// ── AppSidebar ───────────────────────────────────────────────────────────────
export default function AppSidebar({ variant = 'sidebar' }: { variant?: 'sidebar' | 'floating' | 'inset' } = {}) {
  // Cashier's Sales | Ops mode follows the page (see PosModeSwitch).
  const { pathname: currentPath } = useLocation()
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { resolved, setAppearance, accent, setAccent } = useTheme()
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  // Language toggle (AR ⇄ EN) — same single-toggle behaviour as the landing /
  // auth LanguageToggle, but rendered as a top-bar-style icon button below.
  const currentLang = (i18n.language as LangCode) === 'ar' ? 'ar' : 'en'
  const nextLang = LANGUAGES.find(l => l.code !== currentLang) ?? LANGUAGES[0]
  const switchLang = () => {
    i18n.changeLanguage(nextLang.code)
    applyLanguage(nextLang.code as LangCode)
  }
  const navigate = useNavigate()
  // Per-org app entitlements — only the page app gates nav by them. `has`
  // defaults to enabled while loading so the nav doesn't flicker.
  const { has } = useEntitlements({ enabled: IS_PAGE_APP })
  // Per-capability nav gating (signage). Hide a nav item if the role lacks its
  // `perm`; items with no perm always show. While /auth/me is in flight the
  // permissions set is empty, so gate optimistically: show everything until we
  // actually know the role, to avoid a flash of missing nav.
  const { can, role: pRole } = usePermissions()
  const permsKnown = !!pRole
  const navAllowed = (items: { perm?: string }[]) =>
    items.filter(i => !i.perm || !permsKnown || can(i.perm))

  const AUTH_URL = ((import.meta as any).env?.VITE_AUTH_URL as string || '').replace(/\/$/, '')

  // Signing out has to happen even when the server cannot be told about it.
  // This was `logout().then(redirect)` with no rejection path, so a failed
  // request — a phone that lost the venue's wifi, an already-expired session
  // answering 401 — left the promise rejected, the redirect unreached and the
  // menu item looking like a button that does nothing. The local session is
  // cleared and the browser leaves regardless; the server call is best-effort.
  const handleLogout = () => {
    const dest = AUTH_URL ? `${AUTH_URL}/login` : '/login'
    logout()
      .catch(() => { /* best-effort: leaving is the part that matters */ })
      .finally(() => { window.location.href = dest })
  }

  const initials = user?.name
    ? user.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
    : 'AD'

  return (
    <OpenGroupContext.Provider value={{ openGroup, setOpenGroup }}>
    <Sidebar
      side="left"
      collapsible="icon"
      variant={variant}
      // Brand: chrome (sidebar + top bar) is always white, even when the
      // page theme is dark. `brand-chrome-light` resets shadcn tokens to
      // light values inside the wrapper so every child component reading
      // bg-card / text-foreground / text-muted-foreground / border-border
      // automatically gets readable dark-on-white. Defined in each app's
      // src/index.css.
      //
      // `py-1` overrides the vertical half of shadcn's floating `p-2` (twMerge
      // wins on the last padding utility) so the sidebar panel is inset by the
      // same 4px the header + content inset use (`m-1` in AppShell). That lines
      // the sidebar's top edge up with the top bar and its bottom with the
      // content panel. Horizontal `px-2` is left intact so the gap to the
      // content and the collapsed icon-rail width calc are unchanged.
      className="brand-chrome-light border-e border-border py-1"
    >
      {/* ── Header: Brand mark (always centered) ───────────────── */}
      <SidebarHeader className="px-3 group-data-[collapsible=icon]:px-0 pt-5 pb-4 gap-0 items-center">
        <a href="/" className="flex items-center justify-center w-full">
          {/* Per-app brand mark: Frame logo in the Frame (signage) app, the
              neutral monochrome WeTook mark in the My app (follows the chrome
              text colour), and the shared red WeTook mark everywhere else. */}
          {isCurrentApp(APP_URLS.signage) ? (
            // One mark, no wordmark. This used to be three images doing one
            // job: a language-specific lockup (frame-logo-en / -ar, each a
            // COMPLETE mark+wordmark in a single SVG) when expanded, and a
            // separate frame-logo.avif when collapsed. That meant the brand
            // changed shape with the UI language and again with the sidebar
            // state, and any logo change had to be made in three places.
            // frame-mark.svg is the mark alone (4 facet paths, no
            // letterforms), so it reads identically in Arabic and English and
            // needs no swap on collapse — just a smaller height.
            <img
              src="/frame-mark.svg"
              alt="Frame"
              className="h-10 w-auto group-data-[collapsible=icon]:h-8 dark:brightness-0 dark:invert"
            />
          ) : isCurrentApp(APP_URLS.pos) ? (
            // Pocket's own mark, filled with currentColor — black on the light
            // chrome, white on the dark one, from one asset. No collapse swap,
            // for the same reason Frame stopped doing it above.
            <PocketMark className="h-10 w-auto text-sidebar-foreground group-data-[collapsible=icon]:h-7" />
          ) : isCurrentApp(APP_URLS.my) ? (
            <WetookMark className="h-10 w-auto text-sidebar-foreground group-data-[collapsible=icon]:h-7" />
          ) : (
            <WetookMark className={cn('h-8 w-auto group-data-[collapsible=icon]:h-7', IS_PAGE_APP ? 'text-primary' : 'text-sidebar-foreground')} />
          )}
        </a>
      </SidebarHeader>

      {/* ── Nav groups (always flat / always expanded) ─────────────── */}
      <SidebarContent className="py-3 px-2 gap-1.5 overflow-x-hidden">
        {/* Branch ("space") filter — sits directly below the brand mark so the
            active branch context is the first thing the operator sees. Shared
            across all apps; self-hides when the user has no branches. */}
        <BranchFilterCards />

        {/* ── my.wetook ───────────────────────────────────────────── */}
        {IS_MY_APP && (
          <NavGroup
            // Managerly items render optimistically while /auth/me is in
            // flight so they paint together with the rest of the sidebar —
            // only hide them once we KNOW the user isn't a manager.
            items={navGeneralMy.filter(i => !i.managerOnly || !user || MANAGER_ROLES.has(user.role))}
          />
        )}

        {/* ── signage.wetook ──────────────────────────────────────────
            One flat, header-less nav: Overview + Screens, then the content
            authoring items (Collections / Media / Audio / Transitions), then
            Tablet, with Activity + Tutorials trailing. Groups are kept only
            for their spacing — no section labels. */}
        {IS_SIGNAGE_APP && (
          <>
            <NavGroup items={navAllowed(navSignageTop)} />
            <NavGroup items={navAllowed(navSignageContent)} />
            <NavGroup items={navAllowed(navSignageTablet)} />
            <NavGroup items={navAllowed(navSignageBottom)} />
          </>
        )}

        {/* ── pos.wetook ──────────────────────────────────────────────
            Cashier's back office: dashboard, the terminals that used to live
            under My → Devices, and the shared product catalog. Flat and
            header-less like Frame's. */}
        {IS_POS_APP && <PosModeSwitch />}
        {IS_POS_APP && isOpsPath(currentPath) && (
          <>
            <NavGroup items={navAllowed(navPosOps)} />
            <NavGroup
              label={t('nav.procurement', 'Procurement')}
              items={navPosProcurement}
              icon={Truck}
              collapsible
            />
          </>
        )}
        {IS_POS_APP && !isOpsPath(currentPath) && <NavGroup items={navAllowed(navPos)} />}
        {IS_POS_APP && !isOpsPath(currentPath) && (
          <>
            <NavGroup
              label={t('nav.menuGroup', 'Menu')}
              items={navPosMenu}
              icon={UtensilsCrossed}
              collapsible
            />
            <NavGroup
              label={t('nav.marketing', 'Marketing')}
              items={navPosMarketing}
              icon={Tag}
              collapsible
            />
            <NavGroup
              label={t('nav.managementGroup', 'Management')}
              items={navAllowed(navPosManagement)}
              icon={Settings}
              collapsible
            />
          </>
        )}

        {/* ── ping.wetook ─────────────────────────────────────────── */}
        {/* Ping is the ordering front only. Products, branches, customers,
            orders and offers belong to Cashier, which owns those rows and is
            where a web order is accepted — so none of them are managed here. */}
        {IS_PAGE_APP && has('ping') && (
          <NavGroup
            items={navPing}
          />
        )}
      </SidebarContent>

      {/* ── Footer: user account + Language + Appearance + Collapse ─ */}
      <SidebarFooter className="p-2 group-data-[collapsible=icon]:p-1 gap-2">
        {/* User account dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 w-full p-2 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:w-9 group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:justify-center rounded-xl bg-muted hover:bg-sidebar-foreground/10 transition-colors">
              <Avatar className="w-9 h-9 border shadow-sm shrink-0">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                  {user ? initials : <User className="w-4 h-4" />}
                </AvatarFallback>
              </Avatar>
              <div className="group-data-[collapsible=icon]:hidden flex-1 text-start min-w-0">
                <p className="text-[13px] font-bold text-sidebar-foreground truncate leading-none">{user?.name ?? 'User'}</p>
                <p className="text-[11px] text-sidebar-foreground/60 truncate mt-1">{user?.email ?? ''}</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-64 shadow-xl bg-popover border ml-2 mb-2">
            <DropdownMenuLabel className="font-normal py-3">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10 border shadow-sm">
                    <AvatarFallback className="bg-muted text-foreground text-sm font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-[14px] font-bold text-foreground truncate">{user?.name ?? '—'}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{user?.email ?? ''}</p>
                  </div>
                </div>
                <Button size="sm" className="w-full h-8 text-[12px] font-bold bg-primary hover:bg-primary/90 text-primary-foreground border-0 shadow-sm rounded-lg">
                  {t('nav.upgradePlan', 'Upgrade Plan')}
                </Button>
                <Button size="sm" variant="outline" className="w-full h-8 text-[12px] font-bold border-border text-foreground hover:bg-muted bg-card" onClick={() => window.open('https://wa.me/966526111116', '_blank')}>
                  <MessageCircle className="w-3.5 h-3.5 me-2" />
                  {t('common.whatsappSupport', 'WhatsApp Support')}
                </Button>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                // Personal Info always lives in the My app. This dropdown is
                // shared by every app, so a bare navigate() here used to send
                // Frame/Ping users to their OWN app's (nonexistent) /personal-info
                // route instead of My's — same cross-app-vs-local split NavItem
                // already does for the sidebar links above.
                if (IS_MY_APP) navigate('/personal-info')
                else window.location.href = `${APP_URLS.my}/personal-info`
              }}
              className="py-2 font-medium cursor-pointer"
            >
              <UserCircle className="w-4 h-4 me-2" />
              {t('nav.personalInfo', 'Personal Info')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive py-2 font-medium cursor-pointer">
              <LogOut className="w-4 h-4 me-2" />
              {t('auth.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col">
          {/* Language + appearance switchers — token-coloured icon buttons that
              match the top-bar icons (app switcher / notifications) and adapt to
              both light and dark themes. */}
          <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
            {/* Language switcher — icon only, a single toggle (no language code). */}
            <button
              type="button"
              onClick={switchLang}
              aria-label={`Switch language to ${nextLang.label}`}
              className="flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
            >
              <Globe className="w-4 h-4" />
            </button>

            {/* Dark / light mode switcher — shows the mode you'd switch TO. */}
            <button
              type="button"
              onClick={() => setAppearance(resolved === 'dark' ? 'light' : 'dark')}
              aria-label={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
            >
              {resolved === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Accent. It sits with language and light/dark because it is the
                same kind of setting — a personal display preference, not
                something about the organization — and this row is where a
                person already looks for those. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Accent colour"
                  className="flex items-center justify-center w-8 h-8 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                >
                  <Palette className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-52">
                <DropdownMenuLabel>{t('nav.accent', 'Accent colour')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ACCENTS.map(option => (
                  <DropdownMenuItem
                    key={option.key}
                    onClick={() => void setAccent(option.key)}
                    className="gap-2.5"
                  >
                    <span
                      aria-hidden
                      className="w-4 h-4 rounded-full border border-border-control shrink-0"
                      style={{ background: option.swatch }}
                    />
                    <span className="flex-1 min-w-0 truncate">
                      {t(`nav.accent.${option.key}`, option.label)}
                    </span>
                    {accent === option.key && <Check className="w-4 h-4 shrink-0" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Collapse Toggle */}
          <SidebarTrigger className="w-8 h-8 shrink-0 rounded-md hover:bg-sidebar-accent text-sidebar-foreground/60" />
        </div>
      </SidebarFooter>
    </Sidebar>
    </OpenGroupContext.Provider>
  )
}
